import React from 'react';

// Static styles defined outside the component to avoid re-evaluation and re-injection on every render
const printStyles = `
  @page {
      size: A4;
      margin: 8mm;
  }
  body {
      color: #111;
      background-color: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
  }
  @media print {
      html, body {
          background-color: white !important;
          margin: 0 !important;
          padding: 0 !important;
          min-width: 0 !important;
          min-height: 0 !important;
          overflow: visible !important;
      }
      .report-wrapper {
          box-shadow: none !important;
          margin: 0 auto !important;
          padding: 0 !important;
          width: 210mm !important;
          max-width: 100% !important;
          min-height: 0 !important;
      }
      thead { display: table-header-group; }
      tfoot { display: table-footer-group; }
      tr { page-break-inside: avoid; }
  }
  
  /* Consistent Table Borders */
  .table-cell {
      padding: 8px 10px;
      border: 1px solid #d1d5db; /* gray-300 */
      vertical-align: middle;
  }
`;

// INR Currency Formatter Utility
const formatINR = (value) => {
  if (value === null || value === undefined || value === '') return '';
  // Convert string numbers to float, stripping existing commas if any
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  // Return original value if it's not a valid number
  if (isNaN(num)) return value;
  
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

// Quantity Formatter Utility (always 2 decimal places)
const formatQty = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  if (isNaN(num)) return value;
  
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

const StockMovementReport = ({ data }: { data: any }) => {
  // Safe destructuring with optional chaining and fallback empty structures
  const company = data?.company || {};
  const item = data?.item || {};
  const reportMeta = data?.reportMeta || {};
  const transactions = data?.transactions || [];
  const totals = data?.totals || {};
  const footer = data?.footer || {};

  return (
    <>
      {/* Print Optimization Styles */}
      <style dangerouslySetInnerHTML={{ __html: printStyles }} />

      {/* A4 Size Wrapper */}
      <div className="report-wrapper mx-auto bg-white shadow-xl w-[210mm] box-border p-10 flex flex-col font-sans text-[12px] leading-relaxed">
        
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="font-bold text-2xl text-gray-900 uppercase tracking-wide">
            {company?.name || 'Company Name Not Provided'}
          </h1>
          <p className="text-gray-600 text-[13px]">{company?.address}</p>
          <div className="mt-4">
            <h2 className="font-bold text-lg uppercase border-b-2 border-gray-800 inline-block pb-1">
              Stock Movement Report
            </h2>
          </div>
        </div>

        {/* Item Details & Report Meta Section */}
        <div className="flex justify-between items-end mb-6 border border-gray-300 bg-gray-50 p-4 rounded-sm">
          <div className="w-1/2">
            <span className="text-gray-500 text-[11px] uppercase font-bold tracking-wider block mb-1">Item Details</span>
            <h3 className="font-bold text-[18px] text-gray-900">{item?.name || 'Unknown Item'}</h3>
            <p className="text-gray-700 font-medium mt-1">
              Group: <span className="font-normal text-gray-800">{item?.group || '-'}</span>
            </p>
          </div>
            
          <div className="w-1/2 text-right">
            <div className="grid grid-cols-[1fr_120px] gap-y-2 text-[13px] ml-auto w-[250px]">
              <div className="text-gray-500 font-semibold text-right pr-2">Date Range:</div>
              <div className="font-bold text-gray-900 text-left">{reportMeta?.dateRange || '-'}</div>
                    
              <div className="text-gray-500 font-semibold text-right pr-2">Base Unit:</div>
              <div className="font-bold text-gray-900 text-left">{reportMeta?.baseUnit || '-'}</div>
            </div>
          </div>
        </div>

        {/* Core Table Section */}
        <div className="flex-grow">
          <table className="w-full border-collapse border border-gray-300 text-[12px]">
            <thead>
              <tr className="bg-gray-100 text-gray-800 uppercase text-[11px] tracking-wide font-bold">
                <th className="table-cell text-left w-[10%]">Date</th>
                <th className="table-cell text-left w-[18%]">Particulars</th>
                <th className="table-cell text-left w-[12%]">Vch No.</th>
                <th className="table-cell text-right w-[12%]">Inward Qty</th>
                <th className="table-cell text-right w-[12%]">Outward Qty</th>
                <th className="table-cell text-right w-[12%]">Balance Qty</th>
                <th className="table-cell text-right w-[10%]">Rate (₹)</th>
                <th className="table-cell text-right w-[14%]">Value (₹)</th>
              </tr>
            </thead>
            <tbody className="text-gray-800">
              {transactions.length > 0 ? (
                transactions.map((txn, index) => (
                  <tr key={txn?.id || index} className={txn?.isOpening ? "bg-gray-50" : ""}>
                    <td className="table-cell text-left">{txn?.date || '-'}</td>
                    <td className={`table-cell text-left ${txn?.isOpening ? 'italic text-gray-600 font-medium' : 'font-medium'}`}>
                      {txn?.particulars || '-'}
                    </td>
                    <td className={`table-cell text-left ${txn?.isOpening ? 'text-gray-400' : ''}`}>
                      {txn?.vchNo || '-'}
                    </td>
                    <td className={`table-cell text-right ${txn?.inward ? 'text-green-700 font-medium' : ''}`}>
                      {formatQty(txn?.inward)}
                    </td>
                    <td className={`table-cell text-right ${txn?.outward ? 'text-red-700 font-medium' : ''}`}>
                      {formatQty(txn?.outward)}
                    </td>
                    <td className={`table-cell text-right font-bold ${txn?.isOpening ? 'text-gray-900' : ''}`}>
                      {formatQty(txn?.balance)}
                    </td>
                    <td className="table-cell text-right text-gray-600">
                      {formatINR(txn?.rate)}
                    </td>
                    <td className={`table-cell text-right ${txn?.isOpening ? 'font-medium' : ''}`}>
                      {formatINR(txn?.value)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="table-cell text-center text-gray-500 py-8 italic bg-gray-50">
                    No transactions found for the selected period.
                  </td>
                </tr>
              )}
            </tbody>
                
            {/* Totals Section */}
            <tfoot>
              <tr className="bg-gray-100 border-t-2 border-b-2 border-gray-800 text-[13px]">
                <td colSpan={3} className="table-cell text-right font-bold uppercase tracking-wider text-gray-900">Total / Closing Stock</td>
                <td className="table-cell text-right font-bold text-green-800">{formatQty(totals?.inward)}</td>
                <td className="table-cell text-right font-bold text-red-800">{formatQty(totals?.outward)}</td>
                <td className="table-cell text-right font-bold text-gray-900 text-[14px]">{formatQty(totals?.balance)}</td>
                <td className="table-cell bg-gray-200"></td>
                <td className="table-cell text-right font-bold text-gray-900">
                  {formatINR(totals?.value)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Footer Section */}
        <div className="mt-8 pt-4 border-t border-gray-300 flex justify-between items-center text-[11px] text-gray-500">
          <p className="italic">{footer?.disclaimer || ''}</p>
          <p className="font-medium">{footer?.pageInfo || ''}</p>
        </div>

      </div>
    </>
  );
};

export default StockMovementReport;